"use strict";
(function () {
  // --- CHILD COMPONENT: A group of multi-select buttons (NO CHANGES NEEDED) ---
  if (!customElements.get("reviews-drawer-filter")) {
    class ReviewsDrawerFilter extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", (event) => {
          const button = event.target.closest(".reviews-drawer-filter__value");
          if (!button) return;
          button.classList.toggle("active");
          this.dispatchEvent(
            new CustomEvent("filter-change", { bubbles: true, composed: true }),
          );
        });
      }
    }
    customElements.define("reviews-drawer-filter", ReviewsDrawerFilter);
  }

  // --- CHILD COMPONENT: A group of single-select (radio) buttons (NO CHANGES NEEDED) ---
  if (!customElements.get("filter-tags")) {
    class FilterTags extends HTMLElement {
      connectedCallback() {
        this.isDown = false;
        this.startX = 0;
        this.scrollLeftPos = 0;
        this.isDragging = false;

        this.addEventListener("mousedown", (e) => {
          this.isDown = true;
          this.classList.add("is-dragging"); // Useful for CSS cursor: grabbing
          this.startX = e.pageX - this.offsetLeft;
          this.scrollLeftPos = this.scrollLeft;
          this.isDragging = false;
          // Disable snap for smooth dragging
          this.style.scrollSnapType = "none";
        });

        this.addEventListener("mouseleave", () => {
          this.isDown = false;
          this.classList.remove("is-dragging");
          this.style.scrollSnapType = "";
        });

        this.addEventListener("mouseup", () => {
          this.isDown = false;
          this.classList.remove("is-dragging");
          this.style.scrollSnapType = "";
          // Defer resetting isDragging slightly to allow click handler to see it
          setTimeout(() => {
            this.isDragging = false;
          }, 50);
        });

        this.addEventListener("mousemove", (e) => {
          if (!this.isDown) return;
          e.preventDefault();
          const x = e.pageX - this.offsetLeft;
          const walk = (x - this.startX) * 1.5; // Scroll speed factor
          this.scrollLeft = this.scrollLeftPos - walk;

          // If moved significantly, mark as dragging to prevent click
          if (Math.abs(walk) > 5) {
            this.isDragging = true;
          }
        });

        this.addEventListener("click", (event) => {
          if (this.isDragging) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          const button = event.target.closest(".customer-reviews__tag");
          if (!button) return;
          this.querySelectorAll(".customer-reviews__tag").forEach((tag) =>
            tag.classList.remove("active"),
          );
          button.classList.add("active");
          this.dispatchEvent(
            new CustomEvent("filter-change", { bubbles: true, composed: true }),
          );
        });
      }
    }
    customElements.define("filter-tags", FilterTags);
  }

  // --- PARENT CONTROLLER: The main CustomerReviews component ---
  if (customElements.get("customer-reviews")) {
    return;
  }

  class CustomerReviews extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.id.replace("reviews-", "");
      this.data = [];
      this.itemsPerPage = 9;
      this.timeToUpdate = 500;

      this.hideLoading = this.hideLoading.bind(this);
      this._onFilterChange = this._onFilterChange.bind(this);
      this._onPaginationClick = this._onPaginationClick.bind(this);
      this._onClearAll = this._onClearAll.bind(this); //  Bind clear all handler
    }

    connectedCallback() {
      this.setupElements();
      this.addEventListeners();
      this._initFromUrl();
    }

    setupElements() {
      this.data = JSON.parse(
        this.querySelector(`#reviews-data-${this.sectionId}`).textContent,
      );

      this.itemsPerPage = parseInt(this.dataset.itemsPerPage, 10);
      this.grids = this.querySelectorAll(
        `[data-id="customer-reviews-grid-${this.sectionId}"]`,
      );
      this.paginationContainer = this.querySelector(
        `#customer-reviews-pagination-${this.sectionId}`,
      );
      this.clearButtons = document.querySelectorAll(
        '[data-comment="btn-clear-all"]',
      ); // Find clear buttons
      this.videoItemTemplate = this.querySelector(
        '[data-id="review-template-default"]',
      );
      this.iframeItemTemplate = this.querySelector(
        '[data-id="review-template-youtube"]',
      );
    }

    addEventListeners() {
      this.addEventListener("filter-change", this._onFilterChange);
      if (this.paginationContainer) {
        this.paginationContainer.addEventListener(
          "click",
          this._onPaginationClick,
        );
      }
      this.clearButtons.forEach((button) =>
        button.addEventListener("click", this._onClearAll),
      );
    }

    _render() {
      if (!this.grids || !this.paginationContainer) return;

      const filteredItems = this._getFilteredItems();
      const currentPage = this._getCurrentPage();
      const paginatedItems = filteredItems.slice(
        (currentPage - 1) * this.itemsPerPage,
        currentPage * this.itemsPerPage,
      );

      if (this.grids.length > 0) {
        this.grids.forEach((grid, ind) => {
          if (ind == 0) {
            grid.innerHTML = this._generateGridHTML(paginatedItems, "default");
          } else {
            grid.innerHTML = this._generateGridHTML(paginatedItems);
          }
        });
      }
      this.paginationContainer.innerHTML = this._generatePaginationHTML(
        filteredItems.length,
        currentPage,
      );
    }

    /**
     * Reads separate URL params on page load and sets the UI state.
     */
    _initFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const languages = params.get("languages")?.split("+") || [];
      const durations = params.get("durations")?.split("+") || [];
      const trainers = params.get("trainers")?.split("+") || [];
      const tags = params.get("tags")?.split("+") || [];

      const allFilters = [...languages, ...durations, ...trainers, ...tags];

      this.querySelectorAll("[data-value]").forEach((button) => {
        button.classList.toggle(
          "active",
          allFilters.includes(button.dataset.value),
        );
      });

      if (this.querySelector("filter-tags") && tags.length === 0) {
        this.querySelector('[data-value="all"]')?.classList.add("active");
      }

      this._render();
    }

    /**
     * Handles any filter change, builds new URL params, and re-renders.
     */
    _onFilterChange() {
      this.showLoading();
      //  Defer rendering to allow the UI to update with the loader
      setTimeout(() => {
        const activeFilters = this._getActiveFilters();
        const params = new URLSearchParams();

        if (activeFilters.languages.length > 0)
          params.set("languages", activeFilters.languages.join("+"));
        if (activeFilters.durations.length > 0)
          params.set("durations", activeFilters.durations.join("+"));
        if (activeFilters.trainers.length > 0)
          params.set("trainers", activeFilters.trainers.join("+"));
        if (activeFilters.tags.length > 0)
          params.set("tags", activeFilters.tags.join("+"));

        this._updateUrl(params);

        requestAnimationFrame(() => {
          this._render();
          setTimeout(this.hideLoading, 150);
        });
      }, this.timeToUpdate); // A small delay is enough
    }

    /**
     * Handles pagination clicks, preserves filters, and re-renders.
     */
    _onPaginationClick(event) {
      event.preventDefault();
      const link = event.target.closest("a");
      if (!link || link.parentElement.classList.contains("disabled")) return;

      this.showLoading();

      setTimeout(() => {
        const url = new URL(link.href, window.location.origin);
        const page = url.searchParams.get("page");

        if (!page) {
          this.hideLoading(); // Hide loader if page is invalid
          return;
        }

        const activeFilters = this._getActiveFilters();
        const params = new URLSearchParams();
        if (activeFilters.languages.length > 0)
          params.set("languages", activeFilters.languages.join("+"));
        if (activeFilters.durations.length > 0)
          params.set("durations", activeFilters.durations.join("+"));
        if (activeFilters.trainers.length > 0)
          params.set("trainers", activeFilters.trainers.join("+"));
        if (activeFilters.tags.length > 0)
          params.set("tags", activeFilters.tags.join("+"));
        params.set("page", page);

        this._updateUrl(params);
        requestAnimationFrame(() => {
          this._render();
          setTimeout(this.hideLoading, 150);
        });
      }, this.timeToUpdate);
    }

    /**
     * Gets all active filters, categorized by type.
     * @returns {{languages: string[], durations: string[], trainers: string[], tags: string[]}}
     */
    _getActiveFilters() {
      const filters = { languages: [], durations: [], trainers: [], tags: [] };

      this.querySelectorAll(".reviews-drawer-filter__value.active").forEach(
        (button) => {
          const filterGroup = button.closest(".reviews-drawer-filter").dataset
            .name;
          const value = button.dataset.value;
          if (filters[filterGroup]) {
            filters[filterGroup].push(value);
          }
        },
      );

      const activeTag = this.querySelector(".customer-reviews__tag.active");
      if (activeTag && activeTag.dataset.value !== "all") {
        filters.tags.push(activeTag.dataset.value);
      }

      return filters;
    }

    /**
     *  Handles the click on "Clear All" buttons.
     */
    _onClearAll(event) {
      event.preventDefault();
      this.showLoading();

      setTimeout(() => {
        this.querySelectorAll(".reviews-drawer-filter__value.active").forEach(
          (button) => button.classList.remove("active"),
        );
        this.querySelectorAll(".customer-reviews__tag.active").forEach(
          (button) => button.classList.remove("active"),
        );
        this.querySelector('[data-value="all"]')?.classList.add("active");

        this._updateUrl(new URLSearchParams());
        requestAnimationFrame(() => {
          this._render();
          setTimeout(this.hideLoading, 150);
        });
      }, this.timeToUpdate);
    }

    /**
     * Applies cumulative "AND" filtering for all categories.
     */
    _getFilteredItems() {
      const filters = this._getActiveFilters();
      const hasAnyFilter =
        filters.languages.length > 0 ||
        filters.durations.length > 0 ||
        filters.trainers.length > 0 ||
        filters.tags.length > 0;

      // If no filters are active at all, return everything.
      if (!hasAnyFilter) {
        return this.data;
      }

      return this.data.filter((item) => {
        // --- Language "AND" Check ---
        // An item passes if no language filters are selected, OR if its language is in the list.
        const languageMatch =
          filters.languages.length === 0 ||
          filters.languages.includes(item.language);

        // --- Duration "AND" Check ---
        // An item passes if no duration filters are selected, OR if its duration matches AT LEAST ONE of the selected duration ranges.
        const durationMatch =
          filters.durations.length === 0 ||
          filters.durations.some((filterValue) => {
            const duration = +item.duration;
            if (filterValue === "<5 min") return duration < 5;
            if (filterValue === "5-10 min")
              return duration >= 5 && duration <= 10;
            if (filterValue === "10+ min") return duration > 10;
            return false;
          });

        // --- Trainers "AND" Check ---
        // An item passes if no trainer filters are selected, OR if its trainer list contains AT LEAST ONE of the selected trainers.
        const trainerMatch =
          filters.trainers.length === 0 ||
          (item.trainers &&
            item.trainers.some((trainer) =>
              filters.trainers.includes(trainer),
            ));

        // --- Tags "AND" Check ---
        // An item passes if no tag filters are selected, OR if its tag list contains AT LEAST ONE of the selected tags.
        const tagMatch =
          filters.tags.length === 0 ||
          (item.tags && item.tags.some((tag) => filters.tags.includes(tag)));

        // The item is only included if it passes ALL of the above checks.
        return languageMatch && durationMatch && trainerMatch && tagMatch;
      });
    }

    showLoading() {
      this.classList.add("loading");
    }

    hideLoading() {
      this.classList.remove("loading");
    }

    _getCurrentPage() {
      const params = new URLSearchParams(window.location.search);
      return parseInt(params.get("page") || "1", 10);
    }

    _updateUrl(params) {
      let url = this.dataset.pageUrl;
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      history.pushState({ path: url }, "", url);
    }

    _generateGridHTML(items, type) {
      if (items.length === 0)
        return '<p class="customer-reviews__empty-message">No reviews match your filters.</p>';
      // This is a placeholder. You should replace this with your actual render snippet logic.
      return items
        .map((item, ind) => {
          if (type === "default" && !item?.self_hosted_video) {
            return "";
          }
          return this._renderItem(item, ind);
        })
        .join("");
    }

    _generateImage(url, alt) {
      if (!url) return "";
      const imgAlt = alt || "Image";
      return `<img src="${url}" alt="${imgAlt}" />`;
    }

    _generateVideo(url) {
      if (!url) return "";
      return `<video autoplay playsinline><source src="${url}" type="video/mp4"></video>`;
    }

    _generateIframe(videoId, title) {
      if (!videoId) {
        return "<p>YouTube video could not be loaded.</p>";
      }
      const safeTitle = title.replace(/"/g, "&quot;"); // Escape quotes for the title attribute
      return `<iframe src="https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1" class="js-youtube" allow="autoplay; encrypted-media" allowfullscreen title="${safeTitle}"></iframe>`;
    }

    _renderItem(item, ind) {
      if (
        item.video_url &&
        (item.video_url?.includes("youtube.com") ||
          item.video_url?.includes("youtu.be"))
      ) {
        const itemId = "Deferred-Poster-Frame-" + this.sectionId + "-" + ind;
        if (!this.iframeItemTemplate) return ""; // Guard clause

        const clone = this.iframeItemTemplate.content.cloneNode(true);

        // Find all the elements within the cloned template
        const cloneHeading = clone.querySelector(
          '[data-component="review-title"]',
        );
        const duration = clone.querySelector(
          '[data-component="review-duration"]',
        );
        const posterWrapper = clone.querySelector(".customer-review__poster");
        const nestedTemplate = clone.querySelector("template");

        // --- 1. Populate the Nested Template with the iFrame ---
        if (nestedTemplate) {
          let videoId = null;
          const url = item.video_url;

          // Extract the YouTube video ID from various URL formats
          if (url.includes("youtu.be/"))
            videoId = url.split("youtu.be/")[1].split("?")[0];
          else if (url.includes("watch?v="))
            videoId = url.split("v=")[1].split("&")[0];
          else if (url.includes("embed/"))
            videoId = url.split("embed/")[1].split("?")[0];

          // Generate and insert the iframe HTML
          nestedTemplate.innerHTML = this._generateIframe(
            videoId,
            item.title || "YouTube Video",
          );
        }

        // --- 2. Populate Text and Poster ---
        if (cloneHeading) cloneHeading.textContent = item.title || "";
        if (duration)
          duration.textContent = `${item.duration} min${item.duration > 1 ? "s" : ""}`;

        if (posterWrapper) {
          const imageHTML = this._generateImage(
            item.poster_url,
            item.title || "Review Poster",
          );
          posterWrapper.insertAdjacentHTML("beforeend", imageHTML);
          posterWrapper.id = itemId;
        }

        // --- 3. Return the Final HTML String ---
        const tempDiv = document.createElement("div");
        tempDiv.appendChild(clone);
        return tempDiv.innerHTML;
      } else {
        const itemId = "Deferred-Poster-" + this.sectionId + "-" + ind;
        if (!this.videoItemTemplate) return "";

        const clone = this.videoItemTemplate.content.cloneNode(true);

        const cloneHeading = clone.querySelector(
          '[data-component="review-title"]',
        );
        const duration = clone.querySelector(
          '[data-component="review-duration"]',
        );
        const posterWrapper = clone.querySelector(".customer-review__poster");
        const nestedTemplate = clone.querySelector("template"); // Find the <template> tag

        // --- CORRECT WAY TO POPULATE THE NESTED TEMPLATE ---
        if (nestedTemplate) {
          // Get the video URL. Note: Your JSON structure might be different.
          // This assumes your Liquid now provides the full video object.
          const videoId = "review-video-" + this.sectionId + "-" + ind;
          const videoSrc = item.self_hosted_video?.sources[0]?.url || "";

          // Set the .innerHTML of the <template> itself.
          nestedTemplate.innerHTML = this._generateVideo(videoSrc);
          const nestedVideo = nestedTemplate.content.querySelector("video");
          if (nestedVideo) {
            nestedVideo.setAttribute("data-id", videoId);
            nestedVideo.id = videoId;
          }
        }

        if (cloneHeading) cloneHeading.textContent = item.title || "";
        if (duration)
          duration.textContent = `${item.duration} min${item.duration > 1 ? "s" : ""}`;

        if (posterWrapper) {
          const imageHTML = this._generateImage(
            item.poster_url,
            item.title || "Review Poster",
          );
          posterWrapper.insertAdjacentHTML("beforeend", imageHTML);
          posterWrapper.id = itemId;
        }

        // This part remains the same to return the final HTML string
        const tempDiv = document.createElement("div");
        tempDiv.appendChild(clone);

        const videoPlayBtn = tempDiv.querySelector("video-play-btn");
        const videoMuteBtn = tempDiv.querySelector("video-mute-btn");

        const videoId = "review-video-" + this.sectionId + "-" + ind;
        if (videoPlayBtn) videoPlayBtn.setAttribute("data-id", videoId);
        if (videoMuteBtn) videoMuteBtn.setAttribute("data-id", videoId);

        return tempDiv.innerHTML;

        // return `<div class="customer-review-item"><h3>${item.title || ''}</h3><p>Lang: ${item.language}</p><p>Mins: ${item.duration}</p></div>`;
      }
    }

    /**
     * Generates advanced pagination UI with ellipses and disabled states.
     */
    _generatePaginationHTML(totalItems, currentPage) {
      const totalPages = Math.ceil(totalItems / this.itemsPerPage);
      if (totalPages <= 1) return "";

      let html = '<nav class="pagination">';
      const padding = 2; // How many pages to show around the current page

      // --- Previous Button ---
      const prevDisabled = currentPage === 1 ? "disabled" : "";
      html += `<span class="pagination__item pagination__item--arrow ${prevDisabled}"><a href="${this._getPageUrl(currentPage - 1)}"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></a></span>`;

      // --- Page Numbers ---
      let hasRenderedStartEllipsis = false;
      let hasRenderedEndEllipsis = false;

      for (let i = 1; i <= totalPages; i++) {
        const isFirst = i === 1;
        const isLast = i === totalPages;
        const isInPadding =
          i >= currentPage - padding && i <= currentPage + padding;

        if (isFirst) {
          const activeClass =
            i === currentPage ? "pagination__item--current" : "";
          html += `<span class="pagination__item ${activeClass}"><a href="${this._getPageUrl(i)}">${i}</a></span>`;
        } else if (isLast || isInPadding) {
          const activeClass =
            i === currentPage ? "pagination__item--current" : "";
          html += `<span class="pagination__item ${activeClass}"><a href="${this._getPageUrl(i)}">${i}</a></span>`;
        } else if (i < currentPage && !hasRenderedStartEllipsis) {
          html +=
            '<span class="pagination__item pagination__item--dots">...</span>';
          hasRenderedStartEllipsis = true;
        } else if (i > currentPage && !hasRenderedEndEllipsis) {
          html +=
            '<span class="pagination__item pagination__item--dots">...</span>';
          hasRenderedEndEllipsis = true;
        }
      }

      // --- Next Button ---
      const nextDisabled = currentPage === totalPages ? "disabled" : "";
      html += `<span class="pagination__item pagination__item--arrow ${nextDisabled}"><a href="${this._getPageUrl(currentPage + 1)}"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></a></span>`;

      html += "</nav>";
      return html;
    }

    _getPageUrl(pageNumber) {
      const filters = this._getActiveFilters();
      const params = new URLSearchParams();
      if (filters.languages.length > 0)
        params.set("languages", filters.languages.join("+"));
      if (filters.durations.length > 0)
        params.set("durations", filters.durations.join("+"));
      if (filters.trainers.length > 0)
        params.set("trainers", filters.trainers.join("+"));
      if (filters.tags.length > 0) params.set("tags", filters.tags.join("+"));
      params.set("page", pageNumber);

      return `?${params.toString()}`;
    }
  }

  customElements.define("customer-reviews", CustomerReviews);

  if (!customElements.get("reviews-drawer")) {
    class ReviewsDrawer extends HTMLElement {
      constructor() {
        super();
        this.isOpen = false;
        this.drawerId = this.id;
        this.openers = [];
        this.toggle = this.toggle.bind(this);
      }

      connectedCallback() {
        this.setupElements();
        this.addEventListeners();
      }

      disconnectedCallback() {
        this.removeEventListeners();
      }

      disconnectedCallback() {}

      setupElements() {
        if (!this.drawerId) {
          console.error("ReviewsDrawer: The drawer element is missing an ID.");
          return;
        }
        // Find all buttons on the page that are meant to control this specific drawer.
        this.openers = document.querySelectorAll(
          `[data-component="opener"][data-id="${this.drawerId}"]`,
        );
      }

      addEventListeners() {
        this.openers.forEach((opener) => {
          opener.addEventListener("click", this.toggle);
        });
      }

      removeEventListeners() {
        this.openers.forEach((opener) => {
          opener.removeEventListener("click", this.toggle);
        });
      }

      toggle(event) {
        // Prevent the default button action, if any.
        if (event) {
          event.preventDefault();
        }

        // Open if closed, close if open.
        this.isOpen ? this.close() : this.open();
      }

      open() {
        this.classList.add("open");
        // Update aria-expanded on all controlling buttons.
        this.openers.forEach((opener) => {
          opener.setAttribute("aria-expanded", "true");
        });
        this.isOpen = true;
      }

      close() {
        this.classList.remove("open");
        // Update aria-expanded on all controlling buttons.
        this.openers.forEach((opener) => {
          opener.setAttribute("aria-expanded", "false");
        });
        this.isOpen = false;
      }
    }

    customElements.define("reviews-drawer", ReviewsDrawer);
  }
})();
